import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Grid,
} from '@mui/material';
import GenericModal from '../../../components/GenericModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    clearFinanceNotesError,
    createFinanceNote,
    fetchFinanceNotes,
} from '../../../store/slices/financeNotesSlice';
import { fetchFinanceLedger } from '../../../store/slices/financeLedgerSlice';
import { CreateNotePayload } from '../../../services/financeApi';

interface NotesTabProps {
    selectedCustomerId?: number;
    userType?: string;
    customerOptions: Array<{ id: number; name: string }>;
    isClient: boolean;
}

const NotesTab: React.FC<NotesTabProps> = ({ selectedCustomerId, userType, customerOptions, isClient }) => {
    const dispatch = useAppDispatch();
    const notesState = useAppSelector((state) => state.financeNotes);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    React.useEffect(() => {
        if (selectedCustomerId) {
            dispatch(fetchFinanceNotes({ customerId: selectedCustomerId }));
        }
    }, [dispatch, selectedCustomerId]);

    const [noteSuccessMessage, setNoteSuccessMessage] = useState<string | null>(null);
    const [noteDialogOpen, setNoteDialogOpen] = useState<boolean>(false);
    const [noteForm, setNoteForm] = useState<CreateNotePayload>({
        customerId: selectedCustomerId ?? 0,
        amount: 0,
        type: 'Debit',
        description: '',
        attachment: null,
    });

    const handleCreateNote = async () => {
        if (!noteForm.customerId) {
            setNoteSuccessMessage('Select a customer for the note.');
            return;
        }
        try {
            await dispatch(createFinanceNote(noteForm)).unwrap();
            setNoteSuccessMessage('Note created successfully.');
            setNoteDialogOpen(false);
            setNoteForm({
                customerId: selectedCustomerId ?? 0,
                amount: 0,
                type: 'Debit',
                description: '',
                attachment: null,
            });
            dispatch(fetchFinanceNotes({ customerId: selectedCustomerId }));
            if (selectedCustomerId) {
                dispatch(fetchFinanceLedger({ customerId: selectedCustomerId }));
            }
        } catch (error) {
            setNoteSuccessMessage((error as Error).message);
        }
    };

    return (
        <Stack spacing={2}>
            {noteSuccessMessage && (
                <Alert
                    severity={noteSuccessMessage.includes('successfully') ? 'success' : 'error'}
                    onClose={() => setNoteSuccessMessage(null)}
                >
                    {noteSuccessMessage}
                </Alert>
            )}
            {notesState.error && (
                <Alert severity="error" onClose={() => dispatch(clearFinanceNotesError())}>
                    {notesState.error}
                </Alert>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Debit / Credit Notes</Typography>
                {userType !== 'CLIENT' && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            setNoteForm((prev) => ({
                                ...prev,
                                customerId: selectedCustomerId ?? 0,
                            }));
                            setNoteDialogOpen(true);
                        }}
                    >
                        Create Note
                    </Button>
                )}
            </Stack>

            <Card sx={{ overflow: 'hidden' }}>
                <CardContent>
                    {notesState.loadingList && <CircularProgress />}
                    {isMobile ? (
                        <Stack spacing={2}>
                            {notesState.list.map((note) => (
                                <Card key={note.id} variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2">Note #{note.unique_id}</Typography>
                                            <Typography variant="subtitle2" fontWeight="bold">Rs. {note.amount.toLocaleString()}</Typography>
                                        </Box>
                                        <Divider sx={{ mb: 1 }} />
                                        <Grid container spacing={1}>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" color="text.secondary">Type</Typography>
                                                <Typography variant="body2">{note.type}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" color="text.secondary">Customer</Typography>
                                                <Typography variant="body2">{note.client?.name || '—'}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                                <Typography variant="body2">{note.description}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                <Typography variant="body2">Rs. {note.tax.toLocaleString()}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" color="text.secondary">Created By</Typography>
                                                <Typography variant="body2">{note.created_by?.name || '—'}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                {note.attachment && (
                                                    <Button
                                                        size="small"
                                                        href={note.attachment}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        View Attachment
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 800 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Note #</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="right">Tax</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Created By</TableCell>
                                        <TableCell>Attachment</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {notesState.list.map((note) => (
                                        <TableRow key={note.id}>
                                            <TableCell>{note.unique_id}</TableCell>
                                            <TableCell>{note.client?.name || '—'}</TableCell>
                                            <TableCell>{note.type}</TableCell>
                                            <TableCell align="right">
                                                Rs. {note.amount.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                Rs. {note.tax.toLocaleString()}
                                            </TableCell>
                                            <TableCell>{note.description}</TableCell>
                                            <TableCell>{note.created_by?.name || '—'}</TableCell>
                                            <TableCell>
                                                {note.attachment ? (
                                                    <Button
                                                        size="small"
                                                        href={note.attachment}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        View
                                                    </Button>
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <GenericModal
                open={noteDialogOpen}
                onClose={() => setNoteDialogOpen(false)}
                title="Create Debit / Credit Note"
                maxWidth="sm"
                fullWidth
                actions={
                    <>
                        <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCreateNote}>
                            Create Note
                        </Button>
                    </>
                }
            >
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel id="note-type-label">Type</InputLabel>
                        <Select
                            labelId="note-type-label"
                            label="Type"
                            value={noteForm.type}
                            onChange={(e) =>
                                setNoteForm((prev) => ({
                                    ...prev,
                                    type: e.target.value as 'Debit' | 'Credit',
                                }))
                            }
                        >
                            <MenuItem value="Debit">Debit</MenuItem>
                            <MenuItem value="Credit">Credit</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Amount"
                        type="number"
                        value={noteForm.amount}
                        onChange={(e) =>
                            setNoteForm((prev) => ({
                                ...prev,
                                amount: Number(e.target.value),
                            }))
                        }
                        fullWidth
                    />

                    <TextField
                        label="Description"
                        value={noteForm.description}
                        onChange={(e) =>
                            setNoteForm((prev) => ({
                                ...prev,
                                description: e.target.value,
                            }))
                        }
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel id="note-customer-label">Customer</InputLabel>
                        <Select
                            labelId="note-customer-label"
                            label="Customer"
                            value={noteForm.customerId || ''}
                            onChange={(e) =>
                                setNoteForm((prev) => ({
                                    ...prev,
                                    customerId: Number(e.target.value),
                                }))
                            }
                            disabled={isClient}
                        >
                            {customerOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button variant="outlined" component="label">
                        {noteForm.attachment ? 'Replace Attachment' : 'Upload Attachment'}
                        <input
                            hidden
                            type="file"
                            onChange={(e) =>
                                setNoteForm((prev) => ({
                                    ...prev,
                                    attachment: e.target.files?.[0] ?? null,
                                }))
                            }
                        />
                    </Button>
                    {noteForm.attachment && (
                        <Typography variant="caption">{noteForm.attachment.name}</Typography>
                    )}
                </Stack>
            </GenericModal>
        </Stack>
    );
};

export default NotesTab;
